import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Handles the "Remove" action on a grouped order row in the cart
 * (see snippets/cart-products.liquid, .cart-items__order-remove).
 *
 * Removes every line item belonging to the order (identified by their cart
 * line keys, comma-separated in data-dp-order-remove) in a single
 * /cart/update.js request, then dispatches the same cartLinesUpdate event
 * the theme's own cart-items-component uses for external cart mutations
 * (see component-cart-items.js #handleCartUpdate) — every cart-items-component
 * on the page (cart page, drawer) morphs itself from the returned section
 * HTML, and the cart bubble/other listeners update too. No full page reload,
 * and no need to duplicate the theme's own morph/animation logic here.
 */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-dp-order-remove]') : null;
  if (!button || button.disabled) return;

  const keys = button.getAttribute('data-dp-order-remove').split(',').filter(Boolean);
  if (keys.length === 0) return;

  button.disabled = true;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);

  const updates = {};
  keys.forEach((key) => {
    updates[key] = 0;
  });

  const body = JSON.stringify({
    updates,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });

  const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'remove',
      context: 'cart',
      lines: keys.map((key) => ({ id: key, quantity: 0 })),
      promise: deferredUpdatePromise.promise,
    })
  );

  fetch('/cart/update.js', fetchConfig('json', { body }))
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to update cart: ${response.status}`);
      return response.json();
    })
    .then((cart) => {
      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-order-groups',
          didError: false,
        },
      });
    })
    .catch((error) => {
      console.error('[cart-order-groups]', error);
      deferredUpdatePromise.reject(error);
      document.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      button.disabled = false;
    });
});
